import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useRef } from "react";

import { CalcCard, CalcField, CalcRow, CalcStat, usd } from "@/components/site/CalcKit";
import { CalculatorScaffold } from "@/components/site/CalculatorScaffold";
import { buildHead } from "@/lib/seo-head";
import { useShareableCalcState } from "@/lib/calc-share";
import { trackCalcEstimateViewed, trackCalcInput } from "@/lib/calculator-analytics";
import { compareIoVsAmortizing } from "@/lib/investor-calcs";
import ioVsAmortizingHero from "@/assets/hero/hero-interest-only-vs-amortizing-strategy.jpg";

const FAQS = [
  {
    q: "Is interest-only better for a DSCR loan?",
    a: "It lowers the qualifying payment, which raises DSCR and can unlock leverage a fully amortizing payment would not support. The trade is no principal reduction during the interest-only period and a payment step-up when it ends.",
  },
  {
    q: "How long is the interest-only period on investor loans?",
    a: "Commonly the first 10 years of a 30-year term, though 5-year and 7-year structures exist. After it ends the loan re-amortizes over the remaining term, which raises the payment.",
  },
  {
    q: "Does interest-only cost more in total interest?",
    a: "Usually yes over a long hold, because the balance never falls during the interest-only period. Over a short hold the difference is small and the monthly cash flow benefit can outweigh it.",
  },
  {
    q: "Do interest-only loans price higher?",
    a: "Frequently there is a pricing adjustment for the interest-only feature. The size varies by program, leverage, and credit; it is not fixed and is not quoted here.",
  },
  {
    q: "When does amortizing make more sense?",
    a: "Long holds, plans to refinance against a lower balance, and deals where the rent already clears the amortizing DSCR comfortably. Principal paydown is a forced-savings return that shows up as equity at sale.",
  },
  {
    q: "Can I switch from interest-only back to amortizing later?",
    a: "Not mid-term on most fixed structures — the interest-only period and its length are set at closing and the loan converts to fully amortizing automatically at the end of that window. If you want flexibility later, a refinance is generally the way to change the structure.",
  },
  {
    q: "Does interest-only affect my DSCR at the same rent?",
    a: "Yes, holding rent and rate constant, an interest-only payment is lower than the amortizing payment, so DSCR is always higher during the interest-only period. That is why some investors choose interest-only specifically to clear a lender's minimum DSCR threshold.",
  },
  {
    q: "How much higher is the payment after interest-only ends?",
    a: "The balance re-amortizes over whatever term remains, so a 10-year interest-only period on a 30-year loan leaves only 20 years to pay off the full balance, which typically raises the payment noticeably. This calculator shows that step-up so you can plan for it before it happens.",
  },
  {
    q: "Is interest-only riskier for an investor?",
    a: "It shifts risk toward relying on rent growth, refinancing, or sale rather than paydown to build equity, since the balance does not shrink during the interest-only years. It is not inherently unsafe, but it works best paired with a clear exit plan before the payment steps up.",
  },
  {
    q: "Do all DSCR lenders offer interest-only options?",
    a: "No, availability varies by lender and program, and some cap interest-only at lower maximum leverage than fully amortizing loans. Confirm interest-only availability, term length, and any pricing adjustment with the specific program you are considering.",
  },
  {
    q: "Which option builds more equity over five years?",
    a: "A fully amortizing loan builds equity through both principal paydown and any appreciation, while an interest-only loan builds equity from appreciation alone during the interest-only years. Over a short hold the gap is often modest; over a long hold it compounds.",
  },
  {
    q: "Can I make extra principal payments on an interest-only loan?",
    a: "Many notes permit optional principal curtailments even during the interest-only period, which reduces the balance the interest is calculated on going forward. Check the note and any prepayment penalty terms first, since curtailments can still trigger a penalty on some programs.",
  },
];

export const Route = createFileRoute("/interest-only-vs-amortizing-calculator")({
  head: () =>
    buildHead({
      title: "Interest-Only vs Amortizing Calculator | Simply Approved",
      description:
        "Compare interest-only and fully amortizing payments on an investment property loan: monthly cash flow, DSCR, total interest, and principal paid over your hold period.",
      url: "/interest-only-vs-amortizing-calculator",
      keywords:
        "interest only vs amortizing calculator, interest only DSCR loan, IO vs P&I investment property, interest only mortgage comparison, DSCR interest only cash flow, principal paydown calculator, investor loan payment comparison",
      breadcrumbs: [
        { name: "Home", url: "/" },
        { name: "Interest-Only vs Amortizing", url: "/interest-only-vs-amortizing-calculator" },
      ],
      service: { name: "Interest-Only vs Amortizing Calculator" },
      faqs: FAQS,
      extraJsonLd: [
        {
          "@context": "https://schema.org",
          "@type": "WebApplication",
          name: "Interest-Only vs Amortizing Calculator",
          applicationCategory: "FinanceApplication",
          operatingSystem: "Web",
          url: "/interest-only-vs-amortizing-calculator",
          description:
            "Side-by-side comparison of interest-only and amortizing payment structures on investor loans.",
          offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
        },
      ],
    }),
  component: IoPage,
});

type Inputs = {
  loanAmount: number;
  ratePct: number;
  termYears: number;
  holdYears: number;
  monthlyRent: number;
  otherMonthly: number;
  opexMonthly: number;
};

const DEFAULT: Inputs = {
  loanAmount: 320000,
  ratePct: 7.5,
  termYears: 30,
  holdYears: 7,
  monthlyRent: 3000,
  otherMonthly: 560,
  opexMonthly: 300,
};

function IoPage() {
  const { state: i, patch, shareUrl } = useShareableCalcState<Inputs>("io_vs_amortizing", DEFAULT);
  const set = <K extends keyof Inputs>(k: K, v: Inputs[K]) => {
    patch(k, v);
    trackCalcInput("io_vs_amortizing", String(k), v as never);
  };

  const m = useMemo(
    () =>
      compareIoVsAmortizing({
        loanAmount: i.loanAmount,
        ratePct: i.ratePct,
        termYears: i.termYears,
        holdYears: i.holdYears,
        monthlyRent: i.monthlyRent,
        otherMonthly: i.otherMonthly,
        operatingExpensesMonthly: i.opexMonthly,
      }),
    [i],
  );

  const viewed = useRef(false);
  useEffect(() => {
    if (viewed.current) return;
    viewed.current = true;
    trackCalcEstimateViewed("io_vs_amortizing", {
      monthly_difference: m.monthlyDifference,
      hold_years: i.holdYears,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const aiFacts = [
    { label: "Loan amount", value: usd(i.loanAmount) },
    { label: "Interest-only payment", value: usd(m.ioPayment) },
    { label: "Amortizing payment", value: usd(m.amortPayment) },
    { label: "Monthly difference", value: usd(m.monthlyDifference) },
    { label: "DSCR — interest-only", value: (m.ioDscr ?? 0).toFixed(2) },
    { label: "DSCR — amortizing", value: (m.amortDscr ?? 0).toFixed(2) },
    { label: `Principal paid over ${i.holdYears} yrs`, value: usd(m.amortPrincipalPaid) },
  ];

  return (
    <CalculatorScaffold
      calculator="io_vs_amortizing"
      eyebrow="Interest-only vs amortizing"
      title="Interest-Only vs Amortizing Payment Calculator"
      subtitle="Compare both structures on the same loan: monthly payment, cash flow, DSCR, interest paid, and the balance left at the end of your hold. Illustrative estimates only, not a quote or approval."
      shareUrl={shareUrl}
      shareTitle="Interest-only vs amortizing comparison"
      heroImage={{
        src: ioVsAmortizingHero,
        alt: "Rental-property investor comparing interest-only and amortizing payment strategies",
      }}
      inputs={
        <>
          <CalcCard title="1. Loan">
            <div className="grid gap-4 md:grid-cols-2">
              <CalcField label="Loan amount" value={i.loanAmount} onChange={(v) => set("loanAmount", v)} prefix="$" />
              <CalcField label="Note rate" value={i.ratePct} onChange={(v) => set("ratePct", v)} suffix="%" step={0.125} hint="Interest-only structures often price differently — model each separately if you want that difference." />
              <CalcField label="Amortization (years)" value={i.termYears} onChange={(v) => set("termYears", v)} step={5} />
              <CalcField label="Hold period (years)" value={i.holdYears} onChange={(v) => set("holdYears", v)} step={1} />
            </div>
          </CalcCard>
          <CalcCard title="2. Income & carrying costs">
            <div className="grid gap-4 md:grid-cols-2">
              <CalcField label="Monthly rent" value={i.monthlyRent} onChange={(v) => set("monthlyRent", v)} prefix="$" />
              <CalcField label="Taxes + insurance + HOA (monthly)" value={i.otherMonthly} onChange={(v) => set("otherMonthly", v)} prefix="$" hint="The non-P&I part of PITIA, used for DSCR." />
              <CalcField label="Operating expenses (monthly)" value={i.opexMonthly} onChange={(v) => set("opexMonthly", v)} prefix="$" hint="Management, maintenance, reserves. Excluded from DSCR, included in cash flow." />
            </div>
          </CalcCard>
        </>
      }
      results={
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <CalcStat label="Interest-only payment" value={usd(m.ioPayment)} />
            <CalcStat label="Amortizing payment" value={usd(m.amortPayment)} />
            <CalcStat label="DSCR — interest-only" value={(m.ioDscr ?? 0).toFixed(2)} colorClass={(m.ioDscr ?? 0) >= 1.25 ? "text-emerald-600" : "text-amber-600"} />
            <CalcStat label="DSCR — amortizing" value={(m.amortDscr ?? 0).toFixed(2)} colorClass={(m.amortDscr ?? 0) >= 1.25 ? "text-emerald-600" : "text-amber-600"} />
          </div>
          <div className="rounded-xl border border-border bg-card p-5 shadow-sm">
            <h3 className="font-display text-base font-semibold text-foreground">
              Over a {i.holdYears}-year hold
            </h3>
            <dl className="mt-3 space-y-1.5 text-sm">
              <CalcRow label="Monthly payment difference" value={usd(m.monthlyDifference)} strong />
              <CalcRow label="Cash flow — interest-only" value={usd(m.ioCashFlow)} colorClass={m.ioCashFlow >= 0 ? "text-emerald-600" : "text-destructive"} />
              <CalcRow label="Cash flow — amortizing" value={usd(m.amortCashFlow)} colorClass={m.amortCashFlow >= 0 ? "text-emerald-600" : "text-destructive"} />
              <CalcRow label="Interest paid — interest-only" value={usd(m.ioInterestOverPeriod)} />
              <CalcRow label="Interest paid — amortizing" value={usd(m.amortInterestOverPeriod)} />
              <CalcRow label="Principal paid — amortizing" value={usd(m.amortPrincipalPaid)} strong colorClass="text-emerald-600" />
              <CalcRow label="Balance at end — interest-only" value={usd(m.ioBalanceAtEnd)} />
              <CalcRow label="Balance at end — amortizing" value={usd(m.amortBalanceAtEnd)} />
            </dl>
          </div>
        </div>
      }
      aiFacts={aiFacts}
      aiSuggestions={[
        "Which structure fits a 5-year hold?",
        "How much DSCR does interest-only add here?",
        "What happens when the interest-only period ends?",
        "Is principal paydown really a return?",
      ]}
      methodologyIntro="Both columns use the same loan amount and rate so the structural difference is isolated."
      methodology={
        <>
          <p>
            <strong>Payments.</strong> Interest-only is balance × rate ÷ 12. Amortizing is the standard level payment
            over the entered term. Real interest-only programs often carry their own pricing adjustment, which this
            comparison does not assume.
          </p>
          <p>
            <strong>DSCR.</strong> Rent ÷ (payment + taxes, insurance and HOA). Operating expenses are excluded from
            DSCR because most long-term rental programs qualify on gross rent, but they are subtracted in the cash-flow
            lines.
          </p>
          <p>
            <strong>The end of the hold.</strong> The amortizing column shows the balance after your hold period; the
            interest-only column shows the original balance, because none of it has been repaid.
          </p>
        </>
      }
      faqs={FAQS}
      faqTitle="Interest-only vs amortizing FAQ"
      related={[
        { to: "/interest-only-dscr-loans", label: "Interest-only DSCR loans" },
        { to: "/dscr-payment-calculator", label: "DSCR payment (PITIA) calculator" },
        { to: "/rent-needed-for-dscr", label: "Rent needed for a target DSCR" },
        { to: "/dscr-calculator", label: "DSCR calculator" },
        { to: "/prepayment-penalty-calculator", label: "Prepayment penalty calculator" },
        { to: "/cash-on-cash-return-calculator", label: "Cash-on-cash return calculator" },
      ]}
      ctaTitle="Structure the loan around your hold"
      ctaBody="Send the scenario and a licensed loan officer will show how each structure changes leverage and cash flow on business-purpose investment property."
    />
  );
}
